import { Native, env } from '@hb/NBridge/lib/h5';
import { safeJsonStringify, jumpUrl, compareVersions } from '@/utils/tools';
import { getVersion } from '@/utils/systemCode';
import Toast from '@/components/toast';
import eventBus from '@/const/eventBus';


// 加载弹窗
export const loadPlatformAds = async (params = {}) => {
  try {
    // 处理 params 中的 otherInfo
    const result = await Native.callNative({
      classMap: 'hellobike/platform/welfare',
      method: 'loadPlatformAdsPage',
      params,
      callbackId: 'loadHomeWelfareAdsPageCallbackId',
    });
    return result;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('loadPlatformAds => error', safeJsonStringify(error));
    return { errorInfo: error };
  }
};

// 展示弹窗
export const showPlatformAds = async (params = {}) => {
  try {
    const result = await Native.callNative({
      classMap: 'hellobike/platform/welfare',
      method: 'showPlatformAdsPage',
      params,
      callbackId: 'showHomeWelfareAdsPageCallbackId',
      timeout: false,
    });
    return result;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('showPlatformAds => error', safeJsonStringify(error));
    return {};
  }
};

// 消息推送配置获取
export const pushNotificationEnabled = async (params = {}) => {
  try {
    const result = await Native.callNative({
      classMap: 'hellobike/platform/welfare',
      method: 'pushNotificationEnabled',
      params,
    });
    return result || {};
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('pushNotificationEnabled => error', safeJsonStringify(error));
    return {};
  }
};

// 打开系统设置页
export const openSystemSettingPage = async (params = {}) => {
  try {
    let method = '';
    if (env.isIOS) {
      method = 'goSetting';
    } else if (env.isAndroid) {
      method = 'openSystemSettingPage';
    }
    await Native.callNative({
      classMap: 'business/user',
      method,
      params,
    });
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('openSystemSettingPage => error', safeJsonStringify(error));
  }
};

// 商业任务换端h5链接替换
export const mobileAdReplacementWithLink = async (params = {}) => {
  try {
    return Native.callNative({
      classMap: 'MarketingExpose',
      method: 'mobileAdReplacementWithLink',
      params,
      callbackId: 'mobileAdReplacementWithLinkCallbackId',
    });
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('mobileAdReplacementWithLink => error', safeJsonStringify(error));
    return {};
  }
};

// 打开Deeplink
export const openDeepLinkWithParams = async (params = {}) => {
  try {
    return await Native.callNative({
      classMap: 'hellobike/marketing/ads',
      method: 'openDeeplinkWithParams',
      params,
      callbackId: 'openDeeplinkWithParamsCallbackId',
    });
  } catch (error) {
    Toast.show('跳转失败');
    // eslint-disable-next-line no-console
    console.error('openDeepLinkWithParams => error', safeJsonStringify(error));
    return {};
  }
};

// 多链接跳转点击监测bridge
export const openAllAdsURLAndTrack = async (params = {}) => {
  try {
    return await Native.callNative({
      classMap: 'hellobike/marketing/openAllAdsURLAndTrack',
      method: 'openAdsUrlWithTracking',
      params,
    });
  } catch (error) {
    // eslint-disable-next-line no-console
    // console.warn('openAllAdsURLAndTrack => error', safeJsonStringify(error));
    return {};
  }
};

// 曝光监测bridge
export const adsTrackReport = async (params = {}) => {
  try {
    return await Native.callNative({
      classMap: 'hellobike/marketing/openAllAdsURLAndTrack',
      method: 'adsTrackReport',
      params,
      callbackId: 'adsTrackReportCallbackId',
    });
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('adsTrackReport => error', safeJsonStringify(error));
    return {};
  }
};

// 唤端跳转
export const platformOpenScheme = async (params = {}) => {
  const { schema = '', h5 = '' } = params;
  try {
    await Native.callNative({
      method: 'platformOpenScheme',
      params: {
        schemeUrl: schema,
        jumpUrl: h5,
      },
      classMap: 'ui',
    });
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('platformOpenScheme => error', safeJsonStringify(error));
    if (env.isAndroid && h5) {
      jumpUrl(h5);
    }
  }
};

// 添加桌面小组件
export const addWidget = async (params = {}) => {
  try {
    const version = await getVersion();
    if (compareVersions(version, '6.99.15') === -1) return {};
    const result = await Native.callNative({
      classMap: 'hellobike/platform/welfare',
      method: 'addWidget',
      params,
      timeout: false,
    });
    return result || {};
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('addWidget => error', safeJsonStringify(error));
    return {};
  }
};

// 刷新桌面小组件内容
export const updateWidgetContent = async (params = {}) => {
  try {
    const version = await getVersion();
    if (compareVersions(version, '6.99.15') === -1) return {};
    const result = await Native.callNative({
      classMap: 'hellobike/platform/welfare',
      method: 'updateWidgetContent',
      params,
    });
    return result || {};
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('updateWidgetContent => error', safeJsonStringify(error));
    return {};
  }
};

/**
 * 获取设备厂商标识（统一 iOS/Android 差异，调用方直接使用，无需前置判断平台）
 * iOS 固定返回 'IOS'，Android 通过 bridge 获取真实厂商，其他端返回空字符串
 * @returns {Promise<string>} 设备厂商（如 'IOS'、'huawei'、'xiaomi'），获取失败返回空字符串
 */
export const getManufacturer = async () => {
  if (env.isIOS) return 'IOS';
  if (!env.isAndroid) return '';
  try {
    const { brand } = await Native.callNative({
      classMap: 'hellobike/marketing/ads',
      method: 'getBrand',
      params: undefined,
      callbackId: 'getBrandCallbackId',
    });
    return brand || '';
  } catch (error) {
    return '';
  }
};

// 连续观看激励视频流程 — 前端 → 客户端指令
export const platformAdsFlowAction = async (params = {}) => {
  try {
    const result = await Native.callNative({
      classMap: 'hellobike/platform/welfare',
      method: 'platformAdsFlowAction',
      params,
      callbackId: 'platformAdsFlowActionCallbackId',
      timeout: false,
    });
    return result;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('platformAdsFlowAction => error', safeJsonStringify(error));
    return { errCode: -1, errMsg: error && error.message };
  }
};

// 连续激励视频流程 — 客户端 → 前端推送事件，通过 eventBus 分发
window.onPlatformAdsFlowEvent = (params) => {
  eventBus.$emit('platformAdsFlowEvent', params);
};
